import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOMServer from 'react-dom/server';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import styled from 'styled-components';
import EditorToolbar, { formats, getModules } from './Toolbar';
import { useGetAllEmployeesListMutation, useGetShortCodeValidationMutation, } from '../../../features/users/listing/userApiSlice';
import StyledImage from '../styledComponents/StyledImage';
import { getStatusFriendly, handleErrorImg } from '../../../utils/helper';
import JobsIcon from '../svgIcons/JobsIcon';
import theme from '../../../utils/themes';
import CampaignsIcon from '../svgIcons/CampaignsIcon';
import CandidatesIcon from '../svgIcons/CandidatesIcon';
import { useFetchTranslation } from '../../../hooks/useFetchTranslation';
import HiringRequestIcon from '../svgIcons/HiringRequestIcon';
import Profile2User from '../svgIcons/Profile2userIcon';
import { getAddressStringByGoogleLocation } from '../../../features/job/creation/assets/jobCreationUtill';

// Quill blot imports & custom modules
const Embed = Quill.import('blots/embed');
const Clipboard = Quill.import('modules/clipboard');

class PlainClipboard extends Clipboard {
  onPaste(e) {
    e.preventDefault();
    const text = e.clipboardData?.getData('text/plain');
    if (text) {
      const range = this.quill.getSelection();
      this.quill.insertText(range?.index || 0, text, 'user');
      this.quill.setSelection(range.index + text.length, 0, 'silent');
    }
  }
}
Quill.register('modules/clipboard', PlainClipboard, true);

class MentionBlot extends Embed {
  static create(value) {
    const node = super.create();
    node.setAttribute('data-id', value.id);
    node.setAttribute('data-code', `tag-${value.id}`);
    node.setAttribute('data-name', value.name);
    node.classList.add('mention-tag');
    node.innerText = `@${value.name}`;
    return node;
  }

  static value(node) {
    return {
      id: node.getAttribute('data-id'),
      name: node.getAttribute('data-name'),
    };
  }

  length() {
    return 1;
  }

  deleteAt(index, length) {
    super.deleteAt(index, length);
  }
}
MentionBlot.blotName = 'mention';
MentionBlot.tagName = 'span';
MentionBlot.className = 'mention-tag';

class ShortCodeBlot extends Embed {
  static create(value) {
    const node = super.create();
    node.setAttribute('data-code', value.code);
    node.setAttribute('data-label', value.label);
    node.setAttribute('data-url', value.url || '#');
    node.setAttribute('contenteditable', 'false');
    node.classList.add('shortcode-tag');

    const link = document.createElement('a');
    link.href = value.url || '#';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.innerText = value.label;
    link.style.color = '#007bff';
    link.style.textDecoration = 'underline';
    node.appendChild(link);

    return node;
  }

  static value(node) {
    return {
      code: node.getAttribute('data-code'),
      label: node.getAttribute('data-label'),
      url: node.getAttribute('data-url'),
    };
  }
}
ShortCodeBlot.blotName = 'shortcode';
ShortCodeBlot.tagName = 'span';
ShortCodeBlot.className = 'shortcode-tag';

export const getIconByPrefix = (prefix, size = 12) => {
  if (!prefix) return <CandidatesIcon color={theme?.primary} size={size} />;

  switch (prefix) {
    case 'camp':
      return <CampaignsIcon color={theme?.primary} size={size} />;
    case 'job':
      return <JobsIcon border={theme?.primary} size={size} />;
    case 'hp':
      return <HiringRequestIcon fill={theme?.primary} size={size} />;
    case 'app':
      return <Profile2User size={size} />
    default:
      return <CandidatesIcon color={theme?.primary} size={size} />;
  }
};

class IdBlot extends Embed {
  static create(value) {
    const node = super.create();
    if (!value || !value.code || !value.label) {
      node.innerText = value?.code || "";
      return node;
    }

    node.setAttribute('href', value.url || '#');
    node.setAttribute('data-code', value.code);
    node.setAttribute('data-label', value.label);
    node.setAttribute('data-status', value.status || '');
    node.setAttribute('data-data', JSON.stringify(value.data) || '');
    node.setAttribute('data-prefix', value.prefix || '');
    node.setAttribute('data-icon', value.icon || '');
    node.setAttribute('data-url', value.url || '#');
    node.setAttribute('contenteditable', 'false');
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
    node.classList.add('id-tag'); // <-- add main class

    // Wrapper for actual content
    const wrapper = document.createElement('span');
    wrapper.classList.add('id-tag__wrapper');

    // Icon container
    const iconDiv = document.createElement('span');
    iconDiv.classList.add('id-tag__icon');
    // render svg/icon markup as static html
    iconDiv.innerHTML = ReactDOMServer.renderToStaticMarkup(
      <div className="id-icon-inner">
        {getIconByPrefix(value?.code.split('-')[0])}
      </div>
    );

    // Label
    const textDiv = document.createElement('span');
    textDiv.classList.add('id-tag__label');
    textDiv.innerText = value.label;

    // Status (optional)
    if (value.status && value.code.split('-')[0] === 'job') {
      const statusDiv = document.createElement('span');
      statusDiv.classList.add('id-tag__status');
      statusDiv.innerText = value.status.toUpperCase();
      textDiv.appendChild(statusDiv);
    }

    wrapper.appendChild(iconDiv);
    wrapper.appendChild(textDiv);
    node.appendChild(wrapper);

    // Accessibility
    node.setAttribute('role', 'button');
    node.setAttribute('aria-label', `${value.label} — ${value.status || ''}`);
    node.tabIndex = 0;

    return node;
  }


  static value(node) {
    return {
      code: node.getAttribute('data-code'),
      label: node.getAttribute('data-label'),
      status: node.getAttribute('data-status'),
      icon: node.getAttribute('data-icon'),
      data: node.getAttribute('data-data'),
      prefix: node.getAttribute('data-prefix'),
      url: node.getAttribute('data-url') || node.getAttribute('href') || '#',
    };
  }
}
IdBlot.blotName = 'id';
IdBlot.tagName = 'a';

// Register blots once (client-side only)
if (typeof window !== 'undefined') {
  if (!Quill.imports['formats/id']) Quill.register(IdBlot);
  if (!Quill.imports['formats/mention']) Quill.register(MentionBlot);
  if (!Quill.imports['formats/shortcode']) Quill.register(ShortCodeBlot);
}

const StyledTextEditor = ({
  title,
  value,
  onChange,
  placeholder,
  id,
  border,
  borderRadius,
  borderRadiusEditor,
  wrapperBorder,
  elementRef,
  isReadOnly,
  borderTop,
  toolbar,
  noToolBar,
  hide,
  borderApp = '',
  minHeight,
  isInvalid,
  overFlow,
  section,
  mention = false,
  sCode = false,
  mIDs = false,
  ...props
}) => {
  const [getAllEmployeesList] = useGetAllEmployeesListMutation();
  const { fetchTranslation } = useFetchTranslation();
  const [getSCValidation] = useGetShortCodeValidationMutation();

  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [apiUsers, setApiUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  const quillRef = useRef(null);
  const dropdownRef = useRef(null);
  const editorContainerRef = useRef(null);
  const debounceRef = useRef(null);

  // Popover state + refs
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [popoverContent, setPopoverContent] = useState({ code: '', label: '', status: '', url: '', data: {} });
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const popoverTimeoutRef = useRef(null);
  const editorRootRef = useRef(null);
  const fetchUsersFromAPI = useCallback((searchText = '') => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    return new Promise((resolve) => {
      debounceRef.current = setTimeout(async () => {
        setIsLoadingUsers(true);
        try {
          const response = await getAllEmployeesList({
            query: searchText,
          }).unwrap();

          const mappedUsers =
            response.options?.map((user) => ({
              id: user.id,
              name: user.name,
              email: user.email,
            })) || [];

          setApiUsers(mappedUsers);
          resolve(mappedUsers);
        } catch (error) {
          console.error('Error:', error);
          resolve([]);
        } finally {
          setIsLoadingUsers(false);
        }
      }, 300);
    });
  }, [getAllEmployeesList]);

  const isCodeValid = useCallback((code = '') => {
    return new Promise((resolve) => {
      setTimeout(async () => {
        try {
          const payload = { shortcode: code, allowedModule: section };
          const response = await getSCValidation(payload).unwrap();
          resolve(response);
        } catch (error) {
          console.error('Error:', error);
          resolve([]);
        }
      }, 50);
    });
  }, [getSCValidation, section]);

  const users = apiUsers?.length > 0 ? apiUsers : [];

  const handleEditorChange = async (content, delta, source, editor) => {
    if (onChange) {
      onChange(content);
    }

    if (isReadOnly) return;

    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const selection = quill.getSelection();
    if (!selection) return;

    const cursorPosition = selection.index;
    const textBeforeCursor = quill.getText(0, cursorPosition);

    if (mIDs) {
      const lastWordMatch = textBeforeCursor.match(/([a-zA-Z]+-[a-zA-Z0-9]+)\s$/);
      if (lastWordMatch) {
        const fullCode = lastWordMatch[1];
        const [prefix, id] = fullCode.split("-");
        const supportedPrefixes = ["job", "camp", "cand", "hp", 'app'];

        if (supportedPrefixes.includes(prefix)) {
          const label = await fetchTranslation(prefix, id, fullCode);
          if (label) {
            const userTypingPos = quill.getSelection()?.index ?? cursorPosition;
            const replaceStart = cursorPosition - fullCode.length - 1;
            const deleteLength = fullCode.length + 1;

            quill.deleteText(replaceStart, deleteLength, Quill.sources.USER);
            quill.insertEmbed(
              replaceStart,
              "id",
              {
                code: fullCode,
                label: label.label,
                url: `/details/${prefix}/${id}`,
                status: getStatusFriendly(
                  label?.status
                ),
                data: label?.data,
                prefix: prefix
              },
              Quill.sources.USER
            );

            quill.insertText(replaceStart + 1, " ", Quill.sources.USER);
            quill.setSelection(userTypingPos, 0, Quill.sources.SILENT);
          }
        }
      }
    }

    if (mention) {
      const atSymbolIndex = textBeforeCursor.lastIndexOf('@');

      if (atSymbolIndex !== -1) {
        const textAfterAt = textBeforeCursor.substring(atSymbolIndex + 1);

        if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
          const searchText = textAfterAt.toLowerCase();
          let filtered;
          if (getAllEmployeesList) {
            const usersFromAPI = await fetchUsersFromAPI(searchText);
            filtered = usersFromAPI?.filter(
              (user) =>
                user?.name?.toLowerCase()?.includes(searchText) ||
                user?.email?.toLowerCase()?.includes(searchText),
            );
            setFilteredUsers(filtered);
          } else {
            const filteredLocal = users?.filter(
              (user) =>
                user?.name?.toLowerCase()?.includes(searchText) ||
                user?.email?.toLowerCase()?.includes(searchText),
            );
            setFilteredUsers(filteredLocal);
          }
          try {
            setTimeout(() => {
              const updatedSelection = editor.getSelection();
              if (!updatedSelection) return;

              const bounds = editor.getBounds(updatedSelection.index);
              const container =
                editorContainerRef.current?.getBoundingClientRect();

              if (bounds && container) {
                setDropdownPosition({
                  top: bounds.bottom + container.top + 5,
                  left: bounds.left + container.left,
                });

                setShowUserDropdown(filtered?.length > 0);
              }
            }, 10);
          } catch (error) {
            console.error('Error getting cursor position:', error);
            setDropdownPosition({ top: 100, left: 50 });
            setShowUserDropdown(filtered?.length > 0);
          }
          return;
        }
      }
    }

    if (sCode) {
      const lastChar = textBeforeCursor[cursorPosition - 1];
      if (lastChar === ' ') {
        const lastShortcodeMatch = textBeforeCursor
          .substring(0, cursorPosition - 1)
          .match(/\[([^\]]+)\]$/);

        if (lastShortcodeMatch) {
          const fullCode = lastShortcodeMatch[0];

          try {
            const isValid = await isCodeValid(fullCode);
            if (!isValid?.allowed) {
              const quill = quillRef.current?.getEditor();
              if (!quill) return;

              const replaceStart = cursorPosition - 1 - fullCode.length;
              const deleteLength = fullCode.length;

              quill.formatText(replaceStart, deleteLength, { color: 'red' }, Quill.sources.USER);
            }
          } catch (err) {
            console.error('Error validating shortcode:', err);
          }
        }
      }
    }

    setShowUserDropdown(false);
  };

  const handleUserSelect = (item) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const range = quill.getSelection(true);
    if (!range) return;
    const userTypingPos = range.index;
    const textBefore = quill.getText(Math.max(0, range.index - 50), 50);
    const relativeAtIndex = textBefore.lastIndexOf('@');

    let atIndex =
      relativeAtIndex !== -1
        ? range.index - (textBefore.length - relativeAtIndex)
        : range.index;

    const deleteLength = relativeAtIndex !== -1 ? range.index - atIndex : 0;

    if (deleteLength > 0) {
      quill.deleteText(atIndex, deleteLength, Quill.sources.USER);
    }

    const charBefore = quill.getText(atIndex - 1, 1);
    if (charBefore && charBefore !== ' ' && charBefore !== '\n') {
      quill.insertText(atIndex, ' ', Quill.sources.USER);
      atIndex += 1;
    }

    quill.insertEmbed(atIndex, 'mention', item, Quill.sources.USER);
    quill.insertText(atIndex + 1, ' ', Quill.sources.USER);
    quill.setSelection(userTypingPos + 1, 0, Quill.sources.SILENT);

    setShowUserDropdown(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getConditionalProps = (props) => {
    if (props?.publicForm) {
      return { ...props };
    }
    return {};
  };

  const cleanupTimeoutRef = useRef(null);

  useEffect(() => {
    if (!value) return;

    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
    }

    cleanupTimeoutRef.current = setTimeout(() => {
      const editor = quillRef.current?.getEditor();
      if (!editor) return;

      const editorEl = editor.root;
      const allLinks = Array.from(editorEl.querySelectorAll('a[data-code]'));

      if (allLinks.length === 0) return;

      const seenCodes = new Set();
      const linksToRemove = [];

      allLinks.forEach((link) => {
        const code = link.getAttribute('data-code');

        if (seenCodes.has(code)) {
          linksToRemove.push(link);
        } else {
          seenCodes.add(code);
        }
      });

      linksToRemove.forEach((duplicateLink) => {
        const parentP = duplicateLink.closest('p');

        if (parentP) {
          const clone = parentP.cloneNode(true);
          const linkInClone = clone.querySelector('a[data-code]');
          if (linkInClone) linkInClone.remove();

          const remainingText = clone.textContent.trim();

          if (!remainingText) {
            parentP.remove();
          } else {
            duplicateLink.remove();
          }
        } else {
          duplicateLink.remove();
        }
      });

      const wrappedLinks = editorEl.querySelectorAll('p > span > a[data-code]');
      wrappedLinks.forEach((aTag) => {
        const span = aTag.parentElement;

        if (
          span?.tagName === 'SPAN' &&
          span.style.color === 'rgb(18, 18, 18)' &&
          span.style.backgroundColor === 'rgba(54, 126, 202, 0.1)'
        ) {
          span.parentNode.insertBefore(aTag, span);
          span.remove();
        }
      });

      if (linksToRemove.length > 0) {
        editor.update('silent');
      }
    }, 100);

    return () => {
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }
    };
  }, [value]);

  // ---------- Popover helpers & event listeners ----------
  const fetchDetailsForCode = useCallback(async (code) => {
    // Optional: you can fetch additional details here using your fetchTranslation hook.
    // Keeping minimal for now — returns null if not used.
    try {
      const [prefix, id] = (code || '').split('-');
      if (!prefix || !id) return null;
      const details = await fetchTranslation(prefix, id, code);
      return details;
    } catch (err) {
      console.error('fetchDetailsForCode error', err);
      return null;
    }
  }, [fetchTranslation]);

  const showPopoverForElement = async (el) => {
    if (!el) return;
    const code = el.getAttribute('data-code') || el.getAttribute('href') || '';
    const label = el.getAttribute('data-label') || el.textContent || '';
    const status = el.getAttribute('data-status') || '';
    const url = el.getAttribute('data-url') || el.getAttribute('href') || '#';
    let data = el.getAttribute('data-data') || '';
    const prefix = el.getAttribute('data-prefix') || '';
    const rect = el.getBoundingClientRect();
    const top = rect.bottom + window.scrollY + 8;
    const left = rect.left + window.scrollX;


    // ✅ Parse JSON safely once here
    try {
      if (data) {
        data = JSON.parse(data);
          if (typeof data === 'string') {
    data = JSON.parse(data);
  }
      }
    } catch (err) {
      console.warn("Invalid JSON in data-data:", err);
      data = null;
    }

    // Optionally fetch more details and merge
    // const extra = await fetchDetailsForCode(code);
    // if (extra) { merge extra into content }

    setPopoverContent({ code, label, status, url, data, prefix });
    setPopoverPos({ top, left });
    setPopoverVisible(true);
  };

  const hidePopover = () => {
    if (popoverTimeoutRef.current) {
      clearTimeout(popoverTimeoutRef.current);
      popoverTimeoutRef.current = null;
    }
    setPopoverVisible(false);
  };

  // --- REPLACE your existing useEffect that attaches mouse handlers with this block ---
  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    const root = quill.root;
    editorRootRef.current = root;

    // track last anchor code so we can detect changes quickly
    const lastAnchorCodeRef = { current: null };

    const clearPending = () => {
      if (popoverTimeoutRef.current) {
        clearTimeout(popoverTimeoutRef.current);
        popoverTimeoutRef.current = null;
      }
    };

    const handleMouseOver = (ev) => {
      const target = ev.target;
      const anchor = target.closest && target.closest('a[data-code], .id-tag, a.shortcode-tag');
      if (!anchor) return;

      // read its unique id/code
      const code = anchor.getAttribute('data-code') || '';

      // If different anchor than before -> immediately show updated content
      if (lastAnchorCodeRef.current !== code) {
        lastAnchorCodeRef.current = code;
        clearPending();
        // show instantly for snappy UX (small debounce removed for changed anchors)
        showPopoverForElement(anchor);
        return;
      }

      // If same anchor, keep the tiny debounce to avoid flicker
      clearPending();
      popoverTimeoutRef.current = setTimeout(() => {
        showPopoverForElement(anchor);
      }, 120);
    };

    const handleMouseMove = (ev) => {
      const target = ev.target;
      const anchor = target.closest && target.closest('a[data-code], .id-tag, a.shortcode-tag');

      // if moving inside same anchor and popoverVisible -> update position only
      if (anchor) {
        const code = anchor.getAttribute('data-code') || '';
        // if anchor changed while moving, update content immediately
        if (lastAnchorCodeRef.current !== code) {
          lastAnchorCodeRef.current = code;
          clearPending();
          showPopoverForElement(anchor);
          return;
        }

        if (popoverVisible) {
          // update only position based on anchor rect
          const rect = anchor.getBoundingClientRect();
          setPopoverPos({ top: rect.bottom + window.scrollY + 8, left: rect.left + window.scrollX });
        }
        return;
      }

      // moved outside any anchor -> schedule hide
      if (popoverVisible) {
        clearPending();
        popoverTimeoutRef.current = setTimeout(() => {
          lastAnchorCodeRef.current = null;
          hidePopover();
        }, 100);
      }
    };

    const handleMouseOut = (ev) => {
      const fromAnchor = ev.target.closest && ev.target.closest('a[data-code], .id-tag, a.shortcode-tag');
      if (!fromAnchor) return;
      clearPending();
      popoverTimeoutRef.current = setTimeout(() => {
        lastAnchorCodeRef.current = null;
        hidePopover();
      }, 150);
    };

    root.addEventListener('mouseover', handleMouseOver);
    root.addEventListener('mousemove', handleMouseMove);
    root.addEventListener('mouseout', handleMouseOut);

    const onScroll = () => {
      lastAnchorCodeRef.current = null;
      hidePopover();
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);

    return () => {
      root.removeEventListener('mouseover', handleMouseOver);
      root.removeEventListener('mousemove', handleMouseMove);
      root.removeEventListener('mouseout', handleMouseOut);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      clearPending();
    };
  }, [popoverVisible, fetchDetailsForCode]); // keep deps same as before


  const handlePopoverMouseEnter = () => {
    if (popoverTimeoutRef.current) {
      clearTimeout(popoverTimeoutRef.current);
      popoverTimeoutRef.current = null;
    }
  };

  const handlePopoverMouseLeave = () => {
    if (popoverTimeoutRef.current) clearTimeout(popoverTimeoutRef.current);
    popoverTimeoutRef.current = setTimeout(hidePopover, 80);
  };
  // -------------------------------------------------------

  const getBgColor = (statusAttr) => {

    let backgroundColor;
    if (statusAttr === 'Live') {
      backgroundColor = 'rgb(30, 110, 102)';
    } else if (statusAttr === 'Pending') {
      backgroundColor = '#beac43ff';
    } else {
      backgroundColor = '#65ee6cff';
    }
    return backgroundColor
  }


  useEffect(() => {
  if (!elementRef || !quillRef.current) return;

  const editor = quillRef.current.getEditor();

  if (typeof elementRef === 'function') {
    elementRef(editor);
  } else {
    elementRef.current = editor;
  }
}, [elementRef]);

  return (
    <StyledContainer
      {...getConditionalProps(props)}
      borderApp={borderApp}
      overFlow={overFlow}
      border={border}
      isInvalid={isInvalid}
      borderRadius={borderRadius}
      toolbar={toolbar}
      borderTop={borderTop}
      borderRadiusEditor={borderRadiusEditor}
      minHeight={minHeight}
      wrapperBorder={wrapperBorder}
    >
      <h5 className="m-0 fw-bold">{title}</h5>
      <EditorToolbar id={id} noToolBar={noToolBar} hide={hide} />

      <div
        ref={editorContainerRef}
        className="quill-editor-container position-relative"
      >
        <ReactQuill
         ref={quillRef}
          modules={getModules(id, hide)}
          formats={[...formats, mention && 'mention', sCode && 'shortcode', mIDs && 'id']}
          readOnly={isReadOnly}
          value={value}
          onChange={handleEditorChange}
          placeholder={placeholder}
        />

        {mention && showUserDropdown && (
          <UserDropdown
            ref={dropdownRef}
            style={{
              position: 'fixed',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              zIndex: 9999,
            }}
          >
            {isLoadingUsers ? (
              <MentionEmpty>Loading...</MentionEmpty>
            ) : filteredUsers?.length === 0 ? (
              <MentionEmpty>No users found</MentionEmpty>
            ) : (
              filteredUsers.map((user) => (
                <UserOption
                  key={user.id}
                  onClick={() => handleUserSelect(user)}
                >
                  <StyledImage height={'20px'} width={'20px'} className="p-0 me-2">
                    <img
                      src={user?.signedUrl ? user?.signedUrl : process.env.REACT_APP_API_URL + user?.image}
                      alt="img"
                      className="rounded-circle"
                      onError={handleErrorImg}
                    />
                  </StyledImage>
                  <UserName>{user.name}</UserName>
                </UserOption>
              ))
            )}
          </UserDropdown>
        )}

        {/* Popover (renders fixed so it sits on top and follows viewport) */}
        {popoverVisible && (
          <div
            onMouseEnter={handlePopoverMouseEnter}
            onMouseLeave={handlePopoverMouseLeave}
            style={{
              position: 'fixed',
              top: `${popoverPos.top}px`,
              left: `${popoverPos.left}px`,
              zIndex: 12000,
              background: '#fff',
              border: '1px solid rgba(0,0,0,0.08)',
              boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
              padding: '10px 12px',
              borderRadius: 8,
              minWidth: 220,
              fontSize: 13,
              color: '#222',
            }}
          >


            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ fontWeight: 700 }}>  {popoverContent?.prefix !== 'job' ? popoverContent?.data?.job?.title || popoverContent?.label : popoverContent.label || popoverContent.code}</div>
              {popoverContent.status ? (
                <div style={{ marginLeft: 'auto', fontSize: 11, background: `${getBgColor(popoverContent.status)}`, color: '#fff', padding: '2px 8px', borderRadius: 6 }}>
                  {popoverContent.status.toUpperCase()}
                </div>
              ) : null}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {popoverContent?.prefix === 'hp' && popoverContent.label}
              {popoverContent?.prefix === 'camp' &&
                getAddressStringByGoogleLocation(
                  popoverContent.data?.jobHiringPlan?.centralLocation,
                  ['postalTown', 'postalCode']
                )
              } </div>
            {/* <div style={{ marginTop: 6, color: '#666', fontSize: 12 }}>
              <div><strong>Code:</strong> {popoverContent.code}</div>
              {popoverContent.url && (
                <div style={{ marginTop: 6 }}>
                  <a href={popoverContent.url} target="_blank" rel="noopener noreferrer">
                    Open details
                  </a>
                </div>
              )}
            </div> */}
          </div>
        )}
      </div>
    </StyledContainer>
  );
};

// Styled components for dropdown and container (unchanged)
const UserDropdown = styled.div`
  background: ${({ theme }) =>
    theme?.dark ? theme.surface || '#28303A' : '#fff'};
  border-radius: ${({ theme }) => theme.radiusCard || '10px'};
  padding: 8px;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid
    ${({ theme }) =>
      theme?.dark ? 'rgba(255, 255, 255, 0.16)' : 'rgba(13, 35, 73, 0.08)'};
  box-shadow: ${({ theme }) =>
    theme?.dark
      ? '0 12px 28px rgba(0, 0, 0, 0.4)'
      : '0 8px 24px rgba(13, 35, 73, 0.12)'};
  width: auto;
  color: ${({ theme }) => theme.headingColor};
`;

const MentionEmpty = styled.div`
  padding: 12px;
  text-align: center;
  color: ${({ theme }) => theme.inputPlaceHolder || theme.headingColor};
`;

const UserName = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.headingColor};
  font-size: 14px;
`;

const UserOption = styled.div`
  padding: 10px 12px;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.headingColor};

  &:hover {
    background-color: ${({ theme }) =>
      theme?.dark ? theme.fieldLabelBg || '#343B45' : '#f4f5f7'};
    color: ${({ theme }) => theme.headingColor};
  }
`;

const StyledContainer = styled.div`
  background-color: ${({ theme, publicForm }) =>
    publicForm ? 'transparent' : theme?.dark ? theme.fieldLabelBg : theme.white};
  border: ${({ wrapperBorder }) =>
    wrapperBorder ? wrapperBorder : '1px solid transparent'};
  border-radius: 4px;

  .mention-tag {
    background:#E966181F;
    color: #E96618;
    border-radius: 16px;
    padding: 2px 5px;
    margin: 2px 2px;
    display: inline-block;
  }

  .shortcode-tag {
    background: #f1f3f9;
    border-radius: 6px;
    padding: 2px 6px;
    display: inline-block;
  }

  .shortcode-tag a {
    color: #007bff;
    text-decoration: none;
  }

  .shortcode-tag a:hover {
    text-decoration: underline;
  }

  ${({ isValid, theme, isInvalid }) => {
    if (isValid && !isInvalid) {
      return `
      overflow: hidden;
      `;
    }
    if (!isValid && isInvalid) {
      return `
      overflow: hidden;
      border-color: ${theme.danger};
      `;
    }
    return `border-color: transparent`;
  }};
  .ql-editor.ql-blank::before {
    color: var(--bs-calendar-placeholder);
  }

  .quill-editor-container {
    position: relative;
  }

  .quill {
    display: flex;
    flex-direction: column;
    position: relative;
  }

  .ql-container {
    color: ${({ theme }) => (theme?.dark ? theme.headingColor : theme.textColor || 'black')};
  }

  .ql-container.ql-snow {
    border: 1px solid
      ${({ theme }) =>
        theme?.dark ? 'rgba(255, 255, 255, 0.34)' : 'transparent'};
    ${({ theme }) => (theme?.dark ? 'border-top: none;' : '')}
  }
  .ql-toolbar {
    border-radius: ${({ borderRadius }) =>
    borderRadius ? borderRadius : '5px 5px 0 0'};
    background-color: ${({ theme, publicForm }) =>
    publicForm
      ? theme.primary
      : theme?.dark
        ? theme.fieldLabelBg
        : theme.bodyBg} !important;
    border: ${({ border, theme }) =>
    border
      ? border
      : theme?.dark
        ? '1px solid rgba(255, 255, 255, 0.34) !important'
        : '0 !important'};
    ${({ border, theme }) =>
    !border && theme?.dark
      ? 'border-bottom: 1px solid rgba(255, 255, 255, 0.2) !important;'
      : ''}
  }

  &:focus-within .ql-toolbar,
  &:focus-within .ql-container.ql-snow {
    ${({ border, theme }) =>
    !border && theme?.dark
      ? 'border-color: rgba(255, 255, 255, 0.62) !important;'
      : ''}
  }

  &:focus-within .ql-toolbar {
    ${({ border, theme }) =>
    !border && theme?.dark
      ? 'border-bottom-color: rgba(255, 255, 255, 0.28) !important;'
      : ''}
  }

  .ql-snow.ql-toolbar,
  button,
  .ql-stroke {
    display: ${({ toolbar }) => toolbar ?? ''} !important;
    stroke: ${({ theme, publicForm }) =>
    publicForm
      ? theme?.backgroundColor
      : theme.backgroundColor || theme?.dark
        ? theme?.headingColor
        : theme.textColor} !important;

    &:hover {
      color: ${({ theme }) => theme.primary} !important;
    }
  }

  .ql-toolbar button[title='Show HTML source'] {
    width: auto;
    height: auto;
    padding: 2px 8px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.2;
    color: ${({ theme, publicForm }) =>
      publicForm
        ? theme.primaryText || '#ffffff'
        : theme.headingColor || theme.textColor} !important;
    background: transparent !important;

    &:hover,
    &:focus {
      color: ${({ theme, publicForm }) =>
        publicForm
          ? theme.primaryText || '#ffffff'
          : theme.headingColor || theme.textColor} !important;
      background: ${({ publicForm }) =>
        publicForm ? 'rgba(255, 255, 255, 0.18)' : 'rgba(13, 35, 73, 0.08)'} !important;
    }
  }

  .ql-fill {
    fill: ${({ theme }) =>
    theme.backgroundColor || theme?.dark
      ? theme?.primary
      : theme.textColor} !important;
  }

  .ql-editor {
    font-size: 16px !important;
    -webkit-text-size-adjust: 100%;
    font-family: 'Raleway', sans-serif;
    border-radius: ${({ borderRadiusEditor }) =>
    borderRadiusEditor ? borderRadiusEditor : '0 0 5px 5px'};
    min-height: ${({ minHeight }) => (minHeight ? minHeight : '150px')};
    font-size: 14px;
    border-bottom: 2px solid
      ${({ theme, border, publicForm }) =>
    border ? border : publicForm ? theme.primary : theme.inputBorder};
    border-right: 2px solid
      ${({ theme, border, publicForm }) =>
    border ? border : publicForm ? theme.primary : theme.inputBorder};
    border-left: 2px solid
      ${({ theme, border, publicForm }) =>
    border ? border : publicForm ? theme.primary : theme.inputBorder};
    max-height: 300px;
    overflow-y: auto;
    border: ${({ borderApp }) => borderApp && borderApp};
    border-top: ${({ theme, borderTop }) =>
    `${borderTop} solid ${theme.bodyBg}` ?? 'none'} !important;
    color: ${({ publicForm, theme }) =>
    publicForm
      ? theme.primary
      : theme?.dark
        ? theme?.primary
        : theme.textColor};
  }
  .ql-editor p {
    width: fit-content;
    margin-bottom: 14px !important;
    color: ${({ publicForm, theme }) =>
    publicForm
      ? theme.primary
      : theme?.dark
        ? theme?.primary
        : theme.textColor};
  }
`;

export default StyledTextEditor;
